import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  makeStyles,
  Button,
  TextField,
  CircularProgress,
  Paper,
  Typography,
  Box,
  Chip,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Link,
} from "@material-ui/core";
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  OpenInNew as OpenInNewIcon,
} from "@material-ui/icons";
import { useEditor } from "@tldraw/tldraw";
import { createShapeId, AssetRecordType } from "@tldraw/editor";
import { FORMATS, GAP_BETWEEN_PAGES, FONT_FAMILIES } from "pages/flyer/builder/constants";
import { useTldrawApp } from "pages/flyer/builder/drawer/hooks";
import CONFIG from "config/config";
import { useStore } from "../../../../../../../../store";

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  hasError?: boolean;
}

interface AIGenerationProps {
  loading?: boolean;
}

interface DesignValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface UnsplashImage {
  id: string;
  keywords: string;
  description: string;
  width: number;
  height: number;
}

interface GeneratedDesign {
  frontShapes: any[];
  backShapes: any[];
  designConcept?: string;
  colorPalette?: string[];
  unsplashImages?: UnsplashImage[];
}

const AIGenerationComponent: React.FC<AIGenerationProps> = ({ loading }) => {
  const classes = useStyles();
  const app = useTldrawApp();
  const editor = useEditor();
  const {
    campaign: { flyerType },
  } = useStore();

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      text: "Hi! I'm your AI flyer designer. I'll create professional flyers with both front and back pages, featuring mandatory background blocks for visual structure, custom fonts, and high-quality images. Every design includes professional background elements that give your flyer depth and visual appeal. Tell me about your business, event, or promotion!",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastGeneration, setLastGeneration] = useState<GeneratedDesign | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Expert mode state
  const [expertMode, setExpertMode] = useState(false);
  const [customSystemPrompt, setCustomSystemPrompt] = useState("");
  const [temperature, setTemperature] = useState(0.2);
  const [maxTokens, setMaxTokens] = useState(6000);
  const [selectedModel, setSelectedModel] = useState("anthropic/claude-3-5-sonnet-20241022");
  const [customModel, setCustomModel] = useState("");
  const [isCustomModel, setIsCustomModel] = useState(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Safety cleanup effect to reset generating state if component unmounts or remounts
  useEffect(() => {
    return () => {
      if (isGenerating) {
        setIsGenerating(false);
      }
    };
  }, [isGenerating]);

  const getCanvasLayout = useCallback(() => {
    const format = FORMATS[flyerType] || FORMATS.TEARAWAY;
    if (!format) return null;

    const CANVAS_WIDTH = format.width || 419.5;
    const CANVAS_HEIGHT = format.height || 582;

    // Get actual front and back page shapes from the editor
    const frontShape = editor.getShape("shape:front" as any);
    const backShape = editor.getShape("shape:back" as any);

    if (!frontShape || !backShape) {
      // Fallback to default positioning if shapes don't exist
      return {
        format,
        CANVAS_WIDTH,
        CANVAS_HEIGHT,
        FRONT_PAGE_X: 0,
        FRONT_PAGE_Y: 0,
        BACK_PAGE_X: CANVAS_WIDTH + GAP_BETWEEN_PAGES,
        BACK_PAGE_Y: 0,
        GAP_BETWEEN_PAGES,
      };
    }

    // Use actual shape positions
    const FRONT_PAGE_X = frontShape.x;
    const FRONT_PAGE_Y = frontShape.y;
    const BACK_PAGE_X = backShape.x;
    const BACK_PAGE_Y = backShape.y;

    return {
      format,
      CANVAS_WIDTH,
      CANVAS_HEIGHT,
      FRONT_PAGE_X,
      FRONT_PAGE_Y,
      BACK_PAGE_X,
      BACK_PAGE_Y,
      GAP_BETWEEN_PAGES,
      frontShape,
      backShape,
    };
  }, [flyerType, editor]);

  const getEnhancedSystemPrompt = useCallback(() => {
    // If expert mode is enabled and custom prompt is provided, use it
    if (expertMode && customSystemPrompt.trim()) {
      return customSystemPrompt;
    }

    const layout = getCanvasLayout();
    if (!layout) return "";

    const { format, CANVAS_WIDTH, CANVAS_HEIGHT } = layout;
    const availableFonts = Object.keys(FONT_FAMILIES);

    return `You are an expert flyer designer creating professional, high-converting marketing materials. Generate a complete JSON structure for a ${
      format.name
    } flyer (${format.widthInMm}mm x ${format.heightInMm}mm) with BOTH front and back sides.

DESIGN EXCELLENCE PRINCIPLES:
• Create visual hierarchy with clear focal points and proper contrast
• Use professional typography combinations and appropriate sizing
• Implement balanced composition with strategic whitespace
• Apply cohesive color schemes that enhance readability
• Include high-quality images that support the message
• Ensure all elements work together harmoniously

CANVAS SPECIFICATIONS:
• Canvas dimensions: ${CANVAS_WIDTH} × ${CANVAS_HEIGHT} pixels
• Front page coordinates: 0 to ${CANVAS_WIDTH} (width), 0 to ${CANVAS_HEIGHT} (height)
• Back page coordinates: 0 to ${CANVAS_WIDTH} (width), 0 to ${CANVAS_HEIGHT} (height)
• Safe margins: 20px from all edges for critical content
• Bleed area: Account for 3mm print bleed

AVAILABLE FONTS: ${availableFonts.join(", ")}

VALID SHAPE TYPES ONLY:
• customText - for all text elements
• customRect - for backgrounds, boxes, and rectangles 
• customEllipse - for circles and oval shapes
• customImage - for all images (including Unsplash photos)
NEVER use: "image", "unsplashImage", "rectangle", "text" - these are INVALID

REQUIRED JSON STRUCTURE:
{
  "designConcept": "Professional concept description",
  "colorPalette": ["#primary", "#secondary", "#accent", "#text", "#background"],
  "unsplashImages": [
    {
      "id": "image_1",
      "keywords": "business office modern",
      "description": "Modern office workspace",
      "width": 300,
      "height": 200
    }
  ],
  "frontShapes": [
    {
      "id": "front_header_bg",
      "type": "customRect",
      "x": 0,
      "y": 0,
      "props": {
        "w": ${CANVAS_WIDTH},
        "h": 120,
        "fill": "#primary_color",
        "transparency": 0.2,
        "rounded": 0
      }
    },
    {
      "id": "front_content_bg",
      "type": "customRect", 
      "x": 20,
      "y": 140,
      "props": {
        "w": ${CANVAS_WIDTH - 40},
        "h": 200,
        "fill": "#secondary_color",
        "transparency": 0.1,
        "rounded": 8
      }
    },
    {
      "id": "front_footer_bg",
      "type": "customRect",
      "x": 0,
      "y": ${CANVAS_HEIGHT - 80},
      "props": {
        "w": ${CANVAS_WIDTH},
        "h": 80,
        "fill": "#accent_color",
        "transparency": 0.15,
        "rounded": 0
      }
    },
    {
      "id": "front_header_text",
      "type": "customText",
      "x": 30,
      "y": 30,
      "props": {
        "w": ${CANVAS_WIDTH - 60},
        "h": 60,
        "text": "Main Headline",
        "fill": "#000000",
        "size": 36,
        "font": "${availableFonts[0]}",
        "textAlign": "middle",
        "fontWeight": 700,
        "autoSize": true
      }
    },
    {
      "id": "front_body_text",
      "type": "customText",
      "x": 50,
      "y": 400,
      "props": {
        "w": ${CANVAS_WIDTH - 100},
        "h": 100,
        "text": "Body content with good contrast",
        "fill": "#1f2937",
        "size": 16,
        "font": "${availableFonts[1]}",
        "textAlign": "start",
        "fontWeight": 400,
        "autoSize": true
      }
    },
    {
      "id": "front_hero_image",
      "type": "customImage",
      "x": 30,
      "y": 160,
      "props": {
        "w": 300,
        "h": 200,
        "assetId": "image_1",
        "url": "unsplash_url",
        "rounded": 8
      }
    }
  ],
  "backShapes": [ /* same structure */ ]
}

FRONT PAGE STRATEGY (with mandatory backgrounds):
• Header background block: Full-width colored rectangle (0, 0, ${CANVAS_WIDTH}, 120px)
• Hero headline: Bold, large (32-48px), positioned over header background
• Content background sections: Group related elements with background blocks
• Visual anchor: Primary image or logo (300×200px optimal)
• Call-to-action background: Prominent colored block behind CTA text
• Footer background: Contact info background strip (0, ${CANVAS_HEIGHT - 80}, ${CANVAS_WIDTH}, 80px)

BACK PAGE STRATEGY (with mandatory backgrounds):
• Header background: Consistent brand color strip across top
• Service details background: Organized sections with background blocks
• Contact information background: Prominent colored block for contact details
• Secondary images: Supporting visuals with subtle background frames
• Social proof backgrounds: Testimonial sections with background highlights
• Footer background: Terms/conditions with background strip

TYPOGRAPHY HIERARCHY:
• Primary headlines: 36-48px (${availableFonts[0]} bold)
• Secondary headlines: 24-32px (${availableFonts[1]} bold)
• Body text: 16-20px (${availableFonts[2]} regular)
• Supporting text: 14-16px (${availableFonts[3]} regular)
• Fine print: 12-14px (${availableFonts[4]} regular)

POSITIONING STRATEGY (CRITICAL - EXACT BOUNDARIES):
📐 **PAGE COORDINATE SYSTEM:**
• Each page: Width = ${CANVAS_WIDTH}px, Height = ${CANVAS_HEIGHT}px
• Use coordinates from 0 to ${CANVAS_WIDTH} (width) and 0 to ${CANVAS_HEIGHT} (height) for BOTH pages
• Front page elements: x: 0-${CANVAS_WIDTH}, y: 0-${CANVAS_HEIGHT}
• Back page elements: x: 0-${CANVAS_WIDTH}, y: 0-${CANVAS_HEIGHT} (same coordinate system)
• System automatically maps coordinates to actual canvas positions
• Safe print area: Leave 20px margin from all edges (x: 20-${CANVAS_WIDTH - 20}, y: 20-${CANVAS_HEIGHT - 20})

📍 **ELEMENT POSITIONING RULES:**
• Element position + element size MUST stay within page bounds
• For 200px wide element: max x = ${CANVAS_WIDTH - 200} (so x + width ≤ ${CANVAS_WIDTH})
• For 100px tall element: max y = ${CANVAS_HEIGHT - 100} (so y + height ≤ ${CANVAS_HEIGHT})
• Headers: y: 20-${Math.round(CANVAS_HEIGHT * 0.25)} (top area)
• Body content: y: ${Math.round(CANVAS_HEIGHT * 0.25)}-${Math.round(CANVAS_HEIGHT * 0.75)} (middle area)
• Footer/contact: y: ${Math.round(CANVAS_HEIGHT * 0.75)}-${CANVAS_HEIGHT - 20} (bottom area)
• Left margin: x: 20-${Math.round(CANVAS_WIDTH * 0.15)} (left side)
• Center content: x: ${Math.round(CANVAS_WIDTH * 0.15)}-${Math.round(CANVAS_WIDTH * 0.85)} (center)
• Right align: x: ${Math.round(CANVAS_WIDTH * 0.85)}-${CANVAS_WIDTH - 20} (right side)

⚠️ **CRITICAL POSITIONING CHECKS:**
• ALWAYS ensure: x + width ≤ ${CANVAS_WIDTH}
• ALWAYS ensure: y + height ≤ ${CANVAS_HEIGHT}
• NEVER use negative coordinates (x < 0 or y < 0)
• Elements will be automatically placed on correct page (front/back) by the system

IMAGE INTEGRATION:
• Use Unsplash for high-quality stock photos
• Include 2-4 relevant images per design
• Keywords should match business/content type
• Optimize sizes: headers (400×200), backgrounds (full-width), icons (100×100)
• Place images strategically for visual balance

COLOR PSYCHOLOGY & CONTRAST (CRITICAL):
• Professional services: Blues (#2563eb), grays (#6b7280), whites (#ffffff)
• Food/restaurants: Warm oranges (#ea580c), reds (#dc2626), greens (#16a34a)  
• Health/wellness: Calming greens (#059669), blues (#0891b2), naturals (#92400e)
• Events/entertainment: Vibrant purples (#9333ea), pinks (#ec4899), energetic yellows (#eab308)
• Luxury/premium: Deep blacks (#1f2937), golds (#f59e0b), rich purples (#7c3aed)

TEXT CONTRAST RULES (MANDATORY):
• For customText shapes: Use "fill" property for text color (NOT "color")
• Light backgrounds (#ffffff, #f8f9fa, #e5e7eb): Use DARK text fill (#000000, #1f2937, #374151)
• Dark backgrounds (#000000, #1f2937, #374151): Use LIGHT text fill (#ffffff, #f9fafb, #f3f4f6)
• Colored backgrounds: Choose opposite contrast
  - Light blues/greens: Dark text fill (#1f2937)
  - Dark blues/purples: Light text fill (#ffffff)
  - Orange/yellow: Dark text fill (#1f2937)
  - Red/pink: Light text fill (#ffffff)
• Background blocks: Use transparency (0.1-0.3) to maintain readability
• NEVER use similar colors for text and background
• ALWAYS test: Can you easily read the text? If not, change the fill color!

MANDATORY BACKGROUND BLOCKS:
• ALWAYS create background rectangles/blocks for visual foundation
• Header background: Full-width colored rectangle behind title area
• Content sections: Background blocks to group related information
• Footer background: Colored stripe or block for contact information
• Use subtle gradients or solid colors with transparency (0.1-0.3)
• Background colors should complement the main color palette

DESIGN QUALITY STANDARDS:
• Create minimum 8-12 elements per page (including mandatory backgrounds)
• Vary font sizes for clear hierarchy
• Use consistent spacing (8px grid system)
• Ensure 4.5:1 color contrast for accessibility
• Balance visual weight across the layout
• Include both text and visual elements
• Create cohesive front-to-back design flow

CRITICAL REQUIREMENTS - MUST INCLUDE:
1. Header background rectangle (full-width, colored, transparency 0.1-0.3)
2. At least 2 content section background blocks per page
3. Footer background rectangle (full-width, colored)
4. Background blocks MUST be placed BEHIND text elements (lower in the array order)
5. Use transparency values 0.1-0.3 for subtle background effects
6. Background colors from the color palette
7. ALL IMAGES MUST USE "customImage" type - NEVER "image" or "unsplashImage"
8. For images: include both "assetId" and "url" properties in props
9. HIGH CONTRAST TEXT: Use "fill" property for text colors (NOT "color")
10. Dark text fill (#000000, #1f2937) on light backgrounds, light text fill (#ffffff) on dark backgrounds
11. NEVER use white/light text fill on white/light backgrounds
12. NEVER use dark text fill on dark backgrounds
13. Check every text element for readability - if unclear, change the fill color!
14. POSITIONING BOUNDS: Ensure x + width ≤ ${CANVAS_WIDTH} and y + height ≤ ${CANVAS_HEIGHT}
15. NO NEGATIVE COORDINATES: All x, y values must be ≥ 0
16. SAFE MARGINS: Keep 20px margin from edges for professional appearance

⚠️ CRITICAL OUTPUT FORMAT:
• Return ONLY valid JSON - no markdown, no explanations, no comments
• Start with { and end with }
• Use proper JSON syntax: quoted keys, quoted string values, no trailing commas
• Do not include markdown code blocks or formatting
• Do not add any text before or after the JSON
• Ensure all coordinates are within bounds, background blocks are mandatory, and the design is print-ready professional quality.

RETURN FORMAT EXAMPLE:
{"designConcept":"Brief description","colorPalette":["#color1","#color2"],"frontShapes":[...],"backShapes":[...],"unsplashImages":[...]}`;
  }, [getCanvasLayout, expertMode, customSystemPrompt]);

  // Initialize customSystemPrompt with default system prompt
  useEffect(() => {
    const layout = getCanvasLayout();
    if (layout && !customSystemPrompt) {
      const { format, CANVAS_WIDTH, CANVAS_HEIGHT } = layout;
      const availableFonts = Object.keys(FONT_FAMILIES);

      const defaultPrompt = `You are an expert flyer designer creating professional, high-converting marketing materials. Generate a complete JSON structure for a ${
        format.name
      } flyer (${format.widthInMm}mm x ${format.heightInMm}mm) with BOTH front and back sides.

DESIGN EXCELLENCE PRINCIPLES:
• Create visual hierarchy with clear focal points and proper contrast
• Use professional typography combinations and appropriate sizing
• Implement balanced composition with strategic whitespace
• Apply cohesive color schemes that enhance readability
• Include high-quality images that support the message
• Ensure all elements work together harmoniously

CANVAS SPECIFICATIONS:
• Canvas dimensions: ${CANVAS_WIDTH} × ${CANVAS_HEIGHT} pixels
• Front page coordinates: 0 to ${CANVAS_WIDTH} (width), 0 to ${CANVAS_HEIGHT} (height)
• Back page coordinates: 0 to ${CANVAS_WIDTH} (width), 0 to ${CANVAS_HEIGHT} (height)
• Safe margins: 20px from all edges for critical content
• Bleed area: Account for 3mm print bleed

AVAILABLE FONTS: ${availableFonts.join(", ")}

VALID SHAPE TYPES ONLY:
• customText - for all text elements
• customRect - for backgrounds, boxes, and rectangles 
• customEllipse - for circles and oval shapes
• customImage - for all images (including Unsplash photos)
NEVER use: "image", "unsplashImage", "rectangle", "text" - these are INVALID

REQUIRED JSON STRUCTURE:
{
  "designConcept": "Professional concept description",
  "colorPalette": ["#primary", "#secondary", "#accent", "#text", "#background"],
  "unsplashImages": [
    {
      "id": "image_1",
      "keywords": "business office modern",
      "description": "Modern office workspace",
      "width": 300,
      "height": 200
    }
  ],
  "frontShapes": [
    {
      "id": "front_header_bg",
      "type": "customRect",
      "x": 0,
      "y": 0,
      "props": {
        "w": ${CANVAS_WIDTH},
        "h": 120,
        "fill": "#primary_color",
        "transparency": 0.2,
        "rounded": 0
      }
    },
    {
      "id": "front_content_bg",
      "type": "customRect", 
      "x": 20,
      "y": 140,
      "props": {
        "w": ${CANVAS_WIDTH - 40},
        "h": 200,
        "fill": "#secondary_color",
        "transparency": 0.1,
        "rounded": 8
      }
    },
    {
      "id": "front_footer_bg",
      "type": "customRect",
      "x": 0,
      "y": ${CANVAS_HEIGHT - 80},
      "props": {
        "w": ${CANVAS_WIDTH},
        "h": 80,
        "fill": "#accent_color",
        "transparency": 0.15,
        "rounded": 0
      }
    },
    {
      "id": "front_header_text",
      "type": "customText",
      "x": 30,
      "y": 30,
      "props": {
        "w": ${CANVAS_WIDTH - 60},
        "h": 60,
        "text": "Main Headline",
        "fill": "#000000",
        "size": 36,
        "font": "${availableFonts[0]}",
        "textAlign": "middle",
        "fontWeight": 700,
        "autoSize": true
      }
    },
    {
      "id": "front_body_text",
      "type": "customText",
      "x": 50,
      "y": 400,
      "props": {
        "w": ${CANVAS_WIDTH - 100},
        "h": 100,
        "text": "Body content with good contrast",
        "fill": "#1f2937",
        "size": 16,
        "font": "${availableFonts[1]}",
        "textAlign": "start",
        "fontWeight": 400,
        "autoSize": true
      }
    },
    {
      "id": "front_hero_image",
      "type": "customImage",
      "x": 30,
      "y": 160,
      "props": {
        "w": 300,
        "h": 200,
        "assetId": "image_1",
        "url": "unsplash_url",
        "rounded": 8
      }
    }
  ],
  "backShapes": [ /* same structure */ ]
}

FRONT PAGE STRATEGY (with mandatory backgrounds):
• Header background block: Full-width colored rectangle (0, 0, ${CANVAS_WIDTH}, 120px)
• Hero headline: Bold, large (32-48px), positioned over header background
• Content background sections: Group related elements with background blocks
• Visual anchor: Primary image or logo (300×200px optimal)
• Call-to-action background: Prominent colored block behind CTA text
• Footer background: Contact info background strip (0, ${CANVAS_HEIGHT - 80}, ${CANVAS_WIDTH}, 80px)

BACK PAGE STRATEGY (with mandatory backgrounds):
• Header background: Consistent brand color strip across top
• Service details background: Organized sections with background blocks
• Contact information background: Prominent colored block for contact details
• Secondary images: Supporting visuals with subtle background frames
• Social proof backgrounds: Testimonial sections with background highlights
• Footer background: Terms/conditions with background strip

TYPOGRAPHY HIERARCHY:
• Primary headlines: 36-48px (${availableFonts[0]} bold)
• Secondary headlines: 24-32px (${availableFonts[1]} bold)
• Body text: 16-20px (${availableFonts[2]} regular)
• Supporting text: 14-16px (${availableFonts[3]} regular)
• Fine print: 12-14px (${availableFonts[4]} regular)

POSITIONING STRATEGY (CRITICAL - EXACT BOUNDARIES):
📐 **PAGE COORDINATE SYSTEM:**
• Each page: Width = ${CANVAS_WIDTH}px, Height = ${CANVAS_HEIGHT}px
• Use coordinates from 0 to ${CANVAS_WIDTH} (width) and 0 to ${CANVAS_HEIGHT} (height) for BOTH pages
• Front page elements: x: 0-${CANVAS_WIDTH}, y: 0-${CANVAS_HEIGHT}
• Back page elements: x: 0-${CANVAS_WIDTH}, y: 0-${CANVAS_HEIGHT} (same coordinate system)
• System automatically maps coordinates to actual canvas positions
• Safe print area: Leave 20px margin from all edges (x: 20-${CANVAS_WIDTH - 20}, y: 20-${CANVAS_HEIGHT - 20})

📍 **ELEMENT POSITIONING RULES:**
• Element position + element size MUST stay within page bounds
• For 200px wide element: max x = ${CANVAS_WIDTH - 200} (so x + width ≤ ${CANVAS_WIDTH})
• For 100px tall element: max y = ${CANVAS_HEIGHT - 100} (so y + height ≤ ${CANVAS_HEIGHT})
• Headers: y: 20-${Math.round(CANVAS_HEIGHT * 0.25)} (top area)
• Body content: y: ${Math.round(CANVAS_HEIGHT * 0.25)}-${Math.round(CANVAS_HEIGHT * 0.75)} (middle area)
• Footer/contact: y: ${Math.round(CANVAS_HEIGHT * 0.75)}-${CANVAS_HEIGHT - 20} (bottom area)
• Left margin: x: 20-${Math.round(CANVAS_WIDTH * 0.15)} (left side)
• Center content: x: ${Math.round(CANVAS_WIDTH * 0.15)}-${Math.round(CANVAS_WIDTH * 0.85)} (center)
• Right align: x: ${Math.round(CANVAS_WIDTH * 0.85)}-${CANVAS_WIDTH - 20} (right side)

⚠️ **CRITICAL POSITIONING CHECKS:**
• ALWAYS ensure: x + width ≤ ${CANVAS_WIDTH}
• ALWAYS ensure: y + height ≤ ${CANVAS_HEIGHT}
• NEVER use negative coordinates (x < 0 or y < 0)
• Elements will be automatically placed on correct page (front/back) by the system

IMAGE INTEGRATION:
• Use Unsplash for high-quality stock photos
• Include 2-4 relevant images per design
• Keywords should match business/content type
• Optimize sizes: headers (400×200), backgrounds (full-width), icons (100×100)
• Place images strategically for visual balance

COLOR PSYCHOLOGY & CONTRAST (CRITICAL):
• Professional services: Blues (#2563eb), grays (#6b7280), whites (#ffffff)
• Food/restaurants: Warm oranges (#ea580c), reds (#dc2626), greens (#16a34a)  
• Health/wellness: Calming greens (#059669), blues (#0891b2), naturals (#92400e)
• Events/entertainment: Vibrant purples (#9333ea), pinks (#ec4899), energetic yellows (#eab308)
• Luxury/premium: Deep blacks (#1f2937), golds (#f59e0b), rich purples (#7c3aed)

TEXT CONTRAST RULES (MANDATORY):
• For customText shapes: Use "fill" property for text color (NOT "color")
• Light backgrounds (#ffffff, #f8f9fa, #e5e7eb): Use DARK text fill (#000000, #1f2937, #374151)
• Dark backgrounds (#000000, #1f2937, #374151): Use LIGHT text fill (#ffffff, #f9fafb, #f3f4f6)
• Colored backgrounds: Choose opposite contrast
  - Light blues/greens: Dark text fill (#1f2937)
  - Dark blues/purples: Light text fill (#ffffff)
  - Orange/yellow: Dark text fill (#1f2937)
  - Red/pink: Light text fill (#ffffff)
• Background blocks: Use transparency (0.1-0.3) to maintain readability
• NEVER use similar colors for text and background
• ALWAYS test: Can you easily read the text? If not, change the fill color!

MANDATORY BACKGROUND BLOCKS:
• ALWAYS create background rectangles/blocks for visual foundation
• Header background: Full-width colored rectangle behind title area
• Content sections: Background blocks to group related information
• Footer background: Colored stripe or block for contact information
• Use subtle gradients or solid colors with transparency (0.1-0.3)
• Background colors should complement the main color palette

DESIGN QUALITY STANDARDS:
• Create minimum 8-12 elements per page (including mandatory backgrounds)
• Vary font sizes for clear hierarchy
• Use consistent spacing (8px grid system)
• Ensure 4.5:1 color contrast for accessibility
• Balance visual weight across the layout
• Include both text and visual elements
• Create cohesive front-to-back design flow

CRITICAL REQUIREMENTS - MUST INCLUDE:
1. Header background rectangle (full-width, colored, transparency 0.1-0.3)
2. At least 2 content section background blocks per page
3. Footer background rectangle (full-width, colored)
4. Background blocks MUST be placed BEHIND text elements (lower in the array order)
5. Use transparency values 0.1-0.3 for subtle background effects
6. Background colors from the color palette
7. ALL IMAGES MUST USE "customImage" type - NEVER "image" or "unsplashImage"
8. For images: include both "assetId" and "url" properties in props
9. HIGH CONTRAST TEXT: Use "fill" property for text colors (NOT "color")
10. Dark text fill (#000000, #1f2937) on light backgrounds, light text fill (#ffffff) on dark backgrounds
11. NEVER use white/light text fill on white/light backgrounds
12. NEVER use dark text fill on dark backgrounds
13. Check every text element for readability - if unclear, change the fill color!
14. POSITIONING BOUNDS: Ensure x + width ≤ ${CANVAS_WIDTH} and y + height ≤ ${CANVAS_HEIGHT}
15. NO NEGATIVE COORDINATES: All x, y values must be ≥ 0
16. SAFE MARGINS: Keep 20px margin from edges for professional appearance

⚠️ CRITICAL OUTPUT FORMAT:
• Return ONLY valid JSON - no markdown, no explanations, no comments
• Start with { and end with }
• Use proper JSON syntax: quoted keys, quoted string values, no trailing commas
• Do not include markdown code blocks or formatting
• Do not add any text before or after the JSON
• Ensure all coordinates are within bounds, background blocks are mandatory, and the design is print-ready professional quality.

RETURN FORMAT EXAMPLE:
{"designConcept":"Brief description","colorPalette":["#color1","#color2"],"frontShapes":[...],"backShapes":[...],"unsplashImages":[...]}`;

      setCustomSystemPrompt(defaultPrompt);
    }
  }, [flyerType, getCanvasLayout, customSystemPrompt]);

  const validateGeneratedDesign = useCallback(
    (design: any): DesignValidation => {
      const validation: DesignValidation = {
        isValid: true,
        errors: [],
        warnings: [],
      };

      const layout = getCanvasLayout();
      if (!layout) {
        validation.isValid = false;
        validation.errors.push("Cannot determine canvas layout");
        return validation;
      }

      // Check required structure
      if (!design.frontShapes || !Array.isArray(design.frontShapes)) {
        validation.errors.push("Missing or invalid frontShapes array");
      }
      if (!design.backShapes || !Array.isArray(design.backShapes)) {
        validation.errors.push("Missing or invalid backShapes array");
      }

      // Validate mandatory background blocks
      const frontBackgrounds = (design.frontShapes || []).filter(
        (shape: any) => shape.type === "customRect" && shape.id.includes("bg")
      );
      const backBackgrounds = (design.backShapes || []).filter(
        (shape: any) => shape.type === "customRect" && shape.id.includes("bg")
      );

      if (frontBackgrounds.length < 3) {
        validation.errors.push(
          `Front page: Must have at least 3 background blocks (header, content, footer). Found: ${frontBackgrounds.length}`
        );
      }
      if (backBackgrounds.length < 3) {
        validation.errors.push(
          `Back page: Must have at least 3 background blocks (header, content, footer). Found: ${backBackgrounds.length}`
        );
      }

      // Validate shapes
      [...(design.frontShapes || []), ...(design.backShapes || [])].forEach((shape, index) => {
        if (!shape.id || !shape.type || !shape.props) {
          validation.errors.push(`Shape ${index}: Missing required properties (id, type, props)`);
        }

        // Validate shape types
        const validTypes = ["customText", "customRect", "customEllipse", "customImage"];
        if (!validTypes.includes(shape.type)) {
          validation.errors.push(
            `Shape ${index}: Invalid shape type "${shape.type}". Must use: ${validTypes.join(", ")}`
          );
        }

        // Check for common incorrect image types
        if (["image", "unsplashImage", "picture"].includes(shape.type)) {
          validation.errors.push(`Shape ${index}: Use "customImage" instead of "${shape.type}" for all images`);
        }

        // Enhanced boundary validation with element size consideration
        const elementWidth = shape.props?.w || 100;
        const elementHeight = shape.props?.h || 50;

        // Check if element fits within page bounds (including its width/height)
        if (shape.x < 0) {
          validation.errors.push(`Shape ${index}: x coordinate ${shape.x} is negative. Must be >= 0`);
        }

        if (shape.x + elementWidth > layout.CANVAS_WIDTH) {
          validation.errors.push(
            `Shape ${index}: Element extends beyond page width. x: ${shape.x} + width: ${elementWidth} = ${
              shape.x + elementWidth
            } > page width: ${layout.CANVAS_WIDTH}`
          );
        }

        if (shape.y < 0) {
          validation.errors.push(`Shape ${index}: y coordinate ${shape.y} is negative. Must be >= 0`);
        }

        if (shape.y + elementHeight > layout.CANVAS_HEIGHT) {
          validation.errors.push(
            `Shape ${index}: Element extends beyond page height. y: ${shape.y} + height: ${elementHeight} = ${
              shape.y + elementHeight
            } > page height: ${layout.CANVAS_HEIGHT}`
          );
        }

        if (shape.type === "customText" && !shape.props?.text) {
          validation.errors.push(`Text shape ${index}: Missing text content`);
        }

        // Validate text contrast
        if (shape.type === "customText" && shape.props?.fill) {
          const textColor = shape.props.fill.toLowerCase();
          // Check for problematic light text colors
          // if (textColor === "#ffffff" || textColor === "white" || textColor === "#f8f9fa" || textColor === "#f5f5f5" || textColor === "#e5e7eb") {
          //   validation.errors.push(`Text shape ${index}: WHITE/LIGHT TEXT "${textColor}" will be invisible on light backgrounds! Use dark text (#000000, #1f2937, #374151) for better contrast.`);
          // }
          // Check for very light colors that might cause issues
          if (textColor.startsWith("#f") || textColor.startsWith("#e")) {
            validation.warnings.push(
              `Text shape ${index}: Very light text color "${textColor}" may have poor contrast. Consider darker colors for better readability.`
            );
          }
        }

        if (shape.type === "customImage" && !shape.props?.assetId && !shape.props?.url) {
          validation.errors.push(`Image shape ${index}: Missing assetId or url property`);
        }

        // Validate background block transparency
        if (shape.type === "customRect" && shape.id.includes("bg")) {
          if (!shape.props?.transparency || shape.props.transparency < 0.1 || shape.props.transparency > 0.3) {
            validation.warnings.push(
              `Background block ${shape.id}: Should use transparency 0.1-0.3 for professional look`
            );
          }
        }
      });

      validation.isValid = validation.errors.length === 0;
      return validation;
    },
    [getCanvasLayout]
  );

  const createUnsplashAsset = useCallback(
    async (imageData: UnsplashImage, index: number) => {
      try {
        const assetId = AssetRecordType.createId();

        // Use proper Unsplash API to get a real image
        const response = await fetch(
          `${CONFIG.UNSPLASH.unsplashUrl}/search/photos?client_id=${
            CONFIG.UNSPLASH.unsplashAccessKey
          }&query=${encodeURIComponent(imageData.keywords)}&per_page=1&orientation=landscape`
        );

        if (!response.ok) {
          throw new Error(`Unsplash API error: ${response.status}`);
        }

        const data = await response.json();
        const photo = data.results?.[0];

        if (!photo) {
          // Fallback to source.unsplash.com if no results
          const fallbackUrl = `https://source.unsplash.com/${imageData.width}x${imageData.height}/?${encodeURIComponent(
            imageData.keywords
          )}`;

          await editor.createAssets([
            {
              id: assetId,
              type: "image",
              typeName: "asset",
              props: {
                name: imageData.description,
                src: fallbackUrl,
                w: imageData.width,
                h: imageData.height,
                mimeType: "image/jpeg",
                isAnimated: false,
              },
              meta: {
                name: imageData.description,
              },
            },
          ]);

          return { id: imageData.id, assetId, url: fallbackUrl };
        }

        // Use the actual Unsplash photo
        const imageUrl = photo.urls.regular;

        await editor.createAssets([
          {
            id: assetId,
            type: "image",
            typeName: "asset",
            props: {
              name: photo.alt_description || imageData.description,
              src: imageUrl,
              w: imageData.width,
              h: imageData.height,
              mimeType: "image/jpeg",
              isAnimated: false,
            },
            meta: {
              name: photo.alt_description || imageData.description,
            },
          },
        ]);

        return { id: imageData.id, assetId, url: imageUrl };
      } catch (error) {
        console.error(`Error creating Unsplash asset ${index}:`, error);

        // Fallback to source.unsplash.com on any error
        try {
          const assetId = AssetRecordType.createId();
          const fallbackUrl = `https://source.unsplash.com/${imageData.width}x${imageData.height}/?${encodeURIComponent(
            imageData.keywords
          )}`;

          await editor.createAssets([
            {
              id: assetId,
              type: "image",
              typeName: "asset",
              props: {
                name: imageData.description,
                src: fallbackUrl,
                w: imageData.width,
                h: imageData.height,
                mimeType: "image/jpeg",
                isAnimated: false,
              },
              meta: {
                name: imageData.description,
              },
            },
          ]);

          return { id: imageData.id, assetId, url: fallbackUrl };
        } catch (fallbackError) {
          console.error(`Fallback Unsplash creation failed:`, fallbackError);
          return null;
        }
      }
    },
    [editor]
  );

  const createShapeWithValidation = useCallback(
    async (shape: any, isBackPage = false, imageAssets: any = {}) => {
      try {
        const layout = getCanvasLayout();
        if (!layout) throw new Error("Cannot determine canvas layout");

        // Generate proper shape ID
        const shapeId = createShapeId();

        // POSITIONING & BOUNDARY CORRECTION
        let correctedX = shape.x;
        let correctedY = shape.y;
        const elementWidth = shape.props?.w || 100;
        const elementHeight = shape.props?.h || 50;

        // Auto-correct coordinates to stay within page bounds
        if (correctedX < 0) {
          console.warn(`🔧 POSITION FIX: x ${correctedX} → 0 (was negative)`);
          correctedX = 0;
        }

        if (correctedX + elementWidth > layout.CANVAS_WIDTH) {
          const newX = layout.CANVAS_WIDTH - elementWidth;
          console.warn(`🔧 POSITION FIX: x ${correctedX} → ${newX} (element would extend beyond page width)`);
          correctedX = Math.max(0, newX);
        }

        if (correctedY < 0) {
          console.warn(`🔧 POSITION FIX: y ${correctedY} → 0 (was negative)`);
          correctedY = 0;
        }

        if (correctedY + elementHeight > layout.CANVAS_HEIGHT) {
          const newY = layout.CANVAS_HEIGHT - elementHeight;
          console.warn(`🔧 POSITION FIX: y ${correctedY} → ${newY} (element would extend beyond page height)`);
          correctedY = Math.max(0, newY);
        }

        // Map AI coordinates (0 to CANVAS_WIDTH) to actual page positions
        const absoluteX = isBackPage
          ? layout.BACK_PAGE_X + correctedX // Back page: use actual back page X + relative position
          : layout.FRONT_PAGE_X + correctedX; // Front page: use actual front page X + relative position
        const absoluteY = isBackPage
          ? layout.BACK_PAGE_Y + correctedY // Back page: use actual back page Y + relative position
          : layout.FRONT_PAGE_Y + correctedY; // Front page: use actual front page Y + relative position

        // Create base props with proper defaults
        const baseProps: any = {
          w: shape.props?.w || 100,
          h: shape.props?.h || 50,
          fill: shape.props?.fill || "#ffffff",
          outlineColor: shape.props?.outlineColor || "#000000",
          transparency: shape.props?.transparency ?? 1,
          strokeWidth: shape.props?.strokeWidth || 0,
          rounded: shape.props?.rounded || 0,
          filterBlur: 0,
          isCreatedFilterBlur: false,
          filterBlurValueWhenHidden: 0,
          borderWidth: 0,
          borderColor: "#000000",
          borderSolid: "solid",
          borderTopStyle: "solid",
          borderBottomStyle: "solid",
          borderLeftStyle: "solid",
          borderRightStyle: "solid",
          isShowBorderTop: true,
          isShowBorderBottom: true,
          isShowBorderLeft: true,
          isShowBorderRight: true,
          outShadowArray: {},
          inShadowArray: {},
          previousBorderColor: "#000000",
          isCreatedBorderItem: false,
          colorMix: {
            "0": { color: shape.props?.fill || "#ffffff", isHidden: false },
          },
          outlineColorMix: {},
          isHiddenLayerBlur: false,
          isHiddenBorder: false,
          isShowIndicator: false,
          isScale: false,
          scale: 1,
        };

        // Type-specific props
        let finalProps: any = { ...baseProps };

        if (shape.type === "customText") {
          // Auto-fix contrast issues
          let textColor = shape.props?.fill || shape.props?.color || "#000000"; // Check both fill and color for compatibility
          const problematicColors = ["#ffffff", "#f8f9fa", "#f5f5f5", "#e5e7eb", "#f3f4f6", "white"];

          if (
            problematicColors.includes(textColor.toLowerCase()) ||
            textColor.toLowerCase().startsWith("#f") ||
            textColor.toLowerCase().startsWith("#e")
          ) {
            console.warn(`🔧 CONTRAST FIX: Changed light text "${textColor}" to dark "#000000" for better readability`);
            textColor = "#000000";
          }

          finalProps = {
            ...baseProps,
            text: shape.props?.text || "",
            fill: textColor, // FIXED: Use 'fill' for TLDraw text color (not 'color')
            size: Math.max(12, Math.min(72, shape.props?.size || 16)),
            font: shape.props?.font || "Roboto",
            textAlign: shape.props?.textAlign || "start",
            autoSize: shape.props?.autoSize ?? true,
            forAbilityEditable: false,
            fontWeight: shape.props?.fontWeight || 400,
            underline: "none",
            lineThrough: "none",
            italic: "normal",
            textJustify: "none",
            lineHeight: 1.3,
            letterSpacing: 1,
            flipH: false,
            flipV: false,
          };
        } else if (shape.type === "customImage") {
          const imageAsset = imageAssets[shape.props?.assetId];
          if (imageAsset) {
            finalProps = {
              ...baseProps,
              assetId: imageAsset.assetId,
              playing: false,
              url: imageAsset.url,
              crop: null,
              flipX: false,
              flipY: false,
              rounded: shape.props?.rounded || 0,
            };
          }
        }

        // Debug positioning
        console.log(`📍 Creating ${shape.type} on ${isBackPage ? "back" : "front"} page:`, {
          original: { x: shape.x, y: shape.y },
          corrected: { x: correctedX, y: correctedY },
          absolute: { x: absoluteX, y: absoluteY },
          element: { width: elementWidth, height: elementHeight },
          pageOffset: isBackPage
            ? { x: layout.BACK_PAGE_X, y: layout.BACK_PAGE_Y }
            : { x: layout.FRONT_PAGE_X, y: layout.FRONT_PAGE_Y },
          logicalBounds: { maxX: correctedX + elementWidth, maxY: correctedY + elementHeight },
          absoluteBounds: { maxX: absoluteX + elementWidth, maxY: absoluteY + elementHeight },
          page: { width: layout.CANVAS_WIDTH, height: layout.CANVAS_HEIGHT },
          fixed: shape.x !== correctedX || shape.y !== correctedY,
        });

        // Create the shape
        await editor.createShape({
          id: shapeId,
          type: shape.type,
          x: absoluteX,
          y: absoluteY,
          props: finalProps,
        });

        return shapeId;
      } catch (error) {
        console.error("Error creating shape:", error);
        throw error;
      }
    },
    [editor, getCanvasLayout]
  );

  const applyDesignToCanvas = useCallback(
    async (design: GeneratedDesign) => {
      try {
        // Get canvas layout for boundary checking
        const layout = getCanvasLayout();
        if (!layout) throw new Error("Cannot determine canvas layout");

        // Clear existing user-created shapes
        const existingShapes = editor.getCurrentPageShapes();
        if (existingShapes.length > 0) {
          const userShapes = existingShapes.filter(
            (shape: any) =>
              !shape.id.includes("template") &&
              !shape.id.includes("front") &&
              !shape.id.includes("back") &&
              !shape.id.includes("cutmark")
          );
          if (userShapes.length > 0) {
            editor.deleteShapes(userShapes.map((shape: any) => shape.id));
          }
        }

        // Create Unsplash image assets first
        const imageAssets: any = {};
        if (design.unsplashImages) {
          for (let i = 0; i < design.unsplashImages.length; i++) {
            const imageData = design.unsplashImages[i];
            const asset = await createUnsplashAsset(imageData, i);
            if (asset) {
              imageAssets[asset.id] = asset;
            }
          }
        }

        const createdShapes: string[] = [];
        let positionFixesApplied = 0;

        // Create front page shapes
        for (const shape of design.frontShapes) {
          try {
            const shapeId = await createShapeWithValidation(shape, false, imageAssets);
            if (shapeId) {
              createdShapes.push(shapeId);
              // Count if position was auto-corrected
              const elementWidth = shape.props?.w || 100;
              const elementHeight = shape.props?.h || 50;
              const needsXFix = shape.x < 0 || shape.x + elementWidth > layout.CANVAS_WIDTH;
              const needsYFix = shape.y < 0 || shape.y + elementHeight > layout.CANVAS_HEIGHT;
              if (needsXFix || needsYFix) positionFixesApplied++;
            }
          } catch (error) {
            console.error("Error creating front shape:", error);
          }
        }

        // Create back page shapes
        for (const shape of design.backShapes) {
          try {
            const shapeId = await createShapeWithValidation(shape, true, imageAssets);
            if (shapeId) {
              createdShapes.push(shapeId);
              // Count if position was auto-corrected
              const elementWidth = shape.props?.w || 100;
              const elementHeight = shape.props?.h || 50;
              const needsXFix = shape.x < 0 || shape.x + elementWidth > layout.CANVAS_WIDTH;
              const needsYFix = shape.y < 0 || shape.y + elementHeight > layout.CANVAS_HEIGHT;
              if (needsXFix || needsYFix) positionFixesApplied++;
            }
          } catch (error) {
            console.error("Error creating back shape:", error);
          }
        }

        // Select all created shapes and zoom to fit
        if (createdShapes.length > 0) {
          editor.setSelectedShapes(createdShapes as any);
          setTimeout(() => {
            editor.zoomToFit();
          }, 200);
        }

        return {
          shapesCreated: createdShapes.length,
          positioningFixes: positionFixesApplied,
        };
      } catch (error) {
        console.error("Error applying design to canvas:", error);
        throw error;
      }
    },
    [editor, createShapeWithValidation, createUnsplashAsset]
  );

  const handleSendMessage = async () => {
    if (!inputText.trim() || isGenerating) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputText("");
    setIsGenerating(true);

    // Safety timeout to prevent getting stuck in generating state
    const timeoutId = setTimeout(() => {
      setIsGenerating(false);
    }, 60000); // 60 second timeout

    try {
      const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_OPENROUTER_API_KEY}`,
          "HTTP-Referer": window.location.origin,
          "X-Title": "Oppizi Campaign Builder",
        },
        body: JSON.stringify({
          model: isCustomModel ? customModel : selectedModel,
          messages: [
            {
              role: "system",
              content: getEnhancedSystemPrompt(),
            },
            {
              role: "user",
              content: `Create a professional, high-quality flyer design for: ${inputText}. 

Design requirements:
- Use appropriate fonts from available options
- Include relevant Unsplash images 
- Create compelling visual hierarchy
- Ensure perfect positioning and spacing
- Use professional color schemes
- Include both front and back page content
- Make it print-ready quality

Focus on creating a design that converts and looks professionally designed.`,
            },
          ],
          temperature: temperature,
          max_tokens: maxTokens,
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      const aiResponse = data.choices[0]?.message?.content;

      if (!aiResponse) {
        throw new Error("No response content from AI");
      }

      try {
        // Enhanced JSON cleaning and parsing
        let cleanedResponse = aiResponse;

        // Remove markdown code blocks
        cleanedResponse = cleanedResponse.replace(/```json\s*/g, "");
        cleanedResponse = cleanedResponse.replace(/```\s*/g, "");

        // Remove any explanatory text before/after JSON
        cleanedResponse = cleanedResponse.replace(/^[^{]*/, ""); // Remove text before first {
        cleanedResponse = cleanedResponse.replace(/[^}]*$/, ""); // Remove text after last }

        // Remove comments and extra whitespace
        cleanedResponse = cleanedResponse.replace(/\/\*[\s\S]*?\*\//g, ""); // Remove /* */ comments
        cleanedResponse = cleanedResponse.replace(/\/\/.*$/gm, ""); // Remove // comments
        cleanedResponse = cleanedResponse.trim();

        // Log cleaned response for debugging
        console.log("🔍 Cleaned AI response:", cleanedResponse.substring(0, 200) + "...");

        if (!cleanedResponse.startsWith("{") || !cleanedResponse.endsWith("}")) {
          // Try to extract JSON from the response more aggressively
          const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            cleanedResponse = jsonMatch[0];
            console.log("🔧 Extracted JSON from response:", cleanedResponse.substring(0, 100) + "...");
          } else {
            throw new Error(
              `No valid JSON found in response. Expected { ... } structure. Got: "${cleanedResponse.substring(
                0,
                100
              )}..."`
            );
          }
        }

        let designData: GeneratedDesign;
        try {
          designData = JSON.parse(cleanedResponse);
        } catch (jsonError) {
          // Final attempt: try to fix common JSON issues
          const fixedResponse = cleanedResponse
            .replace(/([{,]\s*)(\w+):/g, '$1"$2":') // Add quotes to unquoted keys
            .replace(/:\s*([^",[\]{}]+)(?=\s*[,}])/g, ':"$1"') // Add quotes to unquoted string values
            .replace(/,(\s*[}\]])/g, "$1"); // Remove trailing commas

          console.log("🔧 Attempting JSON repair:", fixedResponse.substring(0, 100) + "...");
          designData = JSON.parse(fixedResponse);
        }

        // Validate the design
        const validation = validateGeneratedDesign(designData);

        if (!validation.isValid) {
          throw new Error(`Design validation failed: ${validation.errors.join(", ")}`);
        }

        // Apply design to canvas
        const result = await applyDesignToCanvas(designData);
        const shapesCreated = result?.shapesCreated || 0;
        const positioningFixes = result?.positioningFixes || 0;
        setLastGeneration(designData);

        // Success message with design details
        let responseText = `✅ Successfully created your professional flyer with ${shapesCreated} elements!`;

        if (positioningFixes > 0) {
          responseText += `\n\n🔧 Auto-fixed ${positioningFixes} positioning issues to ensure perfect page boundaries`;
        }

        // Show actual page alignment info
        const currentLayout = getCanvasLayout();
        if (currentLayout?.frontShape && currentLayout?.backShape) {
          responseText += `\n\n📐 Page Alignment: Front(${Math.round(currentLayout.FRONT_PAGE_X)}, ${Math.round(
            currentLayout.FRONT_PAGE_Y
          )}), Back(${Math.round(currentLayout.BACK_PAGE_X)}, ${Math.round(currentLayout.BACK_PAGE_Y)})`;
        }

        // Count background blocks
        const frontBgs = (designData.frontShapes || []).filter(
          (s: any) => s.type === "customRect" && s.id.includes("bg")
        );
        const backBgs = (designData.backShapes || []).filter(
          (s: any) => s.type === "customRect" && s.id.includes("bg")
        );
        const totalBgs = frontBgs.length + backBgs.length;

        if (totalBgs > 0) {
          responseText += `\n\n📐 Background Structure: ${totalBgs} professional background blocks for visual depth`;
        }

        if (designData.designConcept) {
          responseText += `\n\n🎨 Design Concept: ${designData.designConcept}`;
        }

        if (designData.colorPalette && designData.colorPalette.length > 0) {
          responseText += `\n\n🎨 Color Palette: ${designData.colorPalette.join(" • ")}`;
        }

        if (designData.unsplashImages && designData.unsplashImages.length > 0) {
          responseText += `\n\n📸 Images: ${designData.unsplashImages.length} high-quality Unsplash photos added`;
        }

        if (validation.warnings.length > 0) {
          responseText += `\n\n⚠️ Notes: ${validation.warnings.join(", ")}`;
        }

        responseText +=
          "\n\n💡 Your flyer is print-ready! You can edit, move, resize, or modify any element. Click 'Regenerate' for a new variation!";

        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: responseText,
          isUser: false,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, aiMessage]);
      } catch (parseError) {
        console.error("JSON parsing error:", parseError);
        console.error("Raw AI response that failed to parse:", aiResponse);

        // Provide detailed parsing error info
        let errorDetails = "Failed to parse AI response. ";

        if (parseError instanceof SyntaxError) {
          errorDetails += `JSON syntax error: ${parseError.message}`;
        } else if (parseError instanceof Error) {
          errorDetails += parseError.message;
        } else {
          errorDetails += "Unknown parsing error";
        }

        // Add snippet of problematic response for debugging
        const responseSnippet = aiResponse.substring(0, 200);
        errorDetails += `\n\nResponse preview: "${responseSnippet}${aiResponse.length > 200 ? "..." : ""}"`;

        throw new Error(errorDetails);
      }
    } catch (error) {
      console.error("AI generation error:", error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: `❌ Sorry, I encountered an error: ${
          error instanceof Error ? error.message : "Unknown error"
        }. Please try again with a more specific description.`,
        isUser: false,
        timestamp: new Date(),
        hasError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      clearTimeout(timeoutId);
      setIsGenerating(false);
    }
  };

  const handleRegenerateDesign = async () => {
    if (!lastGeneration || isGenerating) return;

    const lastUserMessage = messages.filter((m) => m.isUser).pop();
    if (lastUserMessage) {
      setInputText(lastUserMessage.text);
      setTimeout(() => {
        handleSendMessage();
      }, 100);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const smartPrompts = [
    "Italian restaurant with warm family atmosphere, authentic cuisine",
    "Real estate listing for modern condo, professional and elegant",
    "Fitness gym grand opening with energetic bold design",
    "Artisanal coffee shop with cozy welcoming atmosphere",
    "Beauty salon with elegant feminine design and luxury feel",
    "Auto repair shop with trustworthy professional appearance",
    "Children's daycare with playful safe colorful environment",
    "Law firm with professional trustworthy sophisticated design",
  ];

  const handleQuickPrompt = (prompt: string) => {
    setInputText(prompt);
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          AI Flyer Designer Pro
        </Typography>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography variant="caption" style={{ color: "#4caf50", fontSize: "0.7rem", fontWeight: 500 }}>
            Wide Mode
          </Typography>
          {lastGeneration && (
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRegenerateDesign}
              disabled={isGenerating}
              className={classes.regenerateButton}
            >
              Regenerate
            </Button>
          )}
        </div>
      </div>

      {/* Expert Mode Controls */}
      <Accordion
        expanded={expertMode}
        onChange={() => setExpertMode(!expertMode)}
        className={classes.expertModeAccordion}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="expert-mode-content" id="expert-mode-header">
          <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
            <SettingsIcon fontSize="small" />
            <Typography variant="subtitle2">Expert Mode</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <div className={classes.expertModeContent}>
            <FormControlLabel
              control={
                <Switch checked={expertMode} onChange={(e) => setExpertMode(e.target.checked)} color="primary" />
              }
              label="Enable Expert Mode"
            />

            {expertMode && (
              <>
                <div className={classes.expertSection}>
                  <Typography variant="subtitle2" gutterBottom>
                    System Prompt
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder="Enter custom system prompt..."
                    value={customSystemPrompt}
                    onChange={(e) => setCustomSystemPrompt(e.target.value)}
                    helperText="Leave empty to use default system prompt"
                    className={classes.expertInput}
                  />
                </div>

                <div className={classes.expertSection}>
                  <Typography variant="subtitle2" gutterBottom>
                    Temperature: {temperature}
                  </Typography>
                  <Slider
                    value={temperature}
                    onChange={(_, value) => setTemperature(value as number)}
                    min={0}
                    max={1}
                    step={0.1}
                    marks={[
                      { value: 0, label: "0" },
                      { value: 0.5, label: "0.5" },
                      { value: 1, label: "1" },
                    ]}
                    valueLabelDisplay="auto"
                    className={classes.expertSlider}
                  />
                  <Typography variant="caption" color="textSecondary">
                    Lower values = more focused, Higher values = more creative
                  </Typography>
                </div>

                <div className={classes.expertSection}>
                  <Typography variant="subtitle2" gutterBottom>
                    Max Tokens: {maxTokens}
                  </Typography>
                  <Slider
                    value={maxTokens}
                    onChange={(_, value) => setMaxTokens(value as number)}
                    min={1000}
                    max={15000}
                    step={500}
                    marks={[
                      { value: 1000, label: "1K" },
                      { value: 4000, label: "4K" },
                      { value: 8000, label: "8K" },
                      { value: 12000, label: "12K" },
                      { value: 15000, label: "15K" },
                    ]}
                    valueLabelDisplay="auto"
                    className={classes.expertSlider}
                  />
                  <Typography variant="caption" color="textSecondary">
                    Maximum response length (higher = more detailed designs)
                  </Typography>
                </div>

                <div className={classes.expertSection}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2">AI Model</Typography>
                    <Link
                      href="https://openrouter.ai/models"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.modelLink}
                    >
                      <OpenInNewIcon fontSize="small" style={{ marginRight: "4px" }} />
                      View All Models
                    </Link>
                  </Box>
                  <FormControl fullWidth variant="outlined" size="medium">
                    <InputLabel>Select Model</InputLabel>
                    <Select
                      value={isCustomModel ? "custom" : selectedModel}
                      onChange={(e) => {
                        const value = e.target.value as string;
                        if (value === "custom") {
                          setIsCustomModel(true);
                        } else {
                          setIsCustomModel(false);
                          setSelectedModel(value);
                        }
                      }}
                      label="Select Model"
                      className={classes.expertSelect}
                    >
                      <MenuItem value="anthropic/claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (Recommended)</MenuItem>
                      <MenuItem value="anthropic/claude-3-opus-20240229">Claude 3 Opus (Most Capable)</MenuItem>
                      <MenuItem value="anthropic/claude-3-haiku-20240307">Claude 3 Haiku (Fastest)</MenuItem>
                      <MenuItem value="openai/gpt-4o">GPT-4o (OpenAI)</MenuItem>
                      <MenuItem value="openai/gpt-4o-mini">GPT-4o Mini (Fast & Cheap)</MenuItem>
                      <MenuItem value="meta-llama/llama-3.1-70b-instruct">Llama 3.1 70B (Open Source)</MenuItem>
                      <MenuItem value="google/gemini-pro-1.5">Gemini Pro 1.5 (Google)</MenuItem>
                      <MenuItem value="custom">Other Model (Free input)</MenuItem>
                    </Select>
                  </FormControl>

                  {isCustomModel && (
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="medium"
                      placeholder="Enter model ID (e.g., openai/gpt-4)"
                      value={customModel}
                      onChange={(e) => setCustomModel(e.target.value)}
                      className={classes.expertInput}
                      style={{ marginTop: "8px" }}
                      helperText="Enter the exact model ID from OpenRouter"
                    />
                  )}
                  <Typography variant="caption" color="textSecondary">
                    Different models have varying capabilities and pricing
                  </Typography>
                </div>
              </>
            )}
          </div>
        </AccordionDetails>
      </Accordion>

      <Paper className={classes.messagesContainer}>
        <div className={classes.messages}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`${classes.message} ${
                message.isUser ? classes.userMessage : message.hasError ? classes.errorMessage : classes.aiMessage
              }`}
            >
              <Typography variant="body2" className={classes.messageText}>
                {message.text}
              </Typography>
              <Typography variant="caption" className={classes.timestamp}>
                {message.timestamp.toLocaleTimeString()}
              </Typography>
            </div>
          ))}
          {isGenerating && (
            <div className={`${classes.message} ${classes.aiMessage}`}>
              <Box display="flex" alignItems="center">
                <CircularProgress size={16} className={classes.loadingSpinner} />
                <Typography variant="body2" className={classes.messageText}>
                  Creating your professional design with custom fonts and images...
                </Typography>
              </Box>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </Paper>

      <div className={classes.inputContainer}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Describe your business, event, or promotion..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isGenerating}
          className={classes.input}
          multiline
          rows={2}
          helperText={`Format: ${FORMATS[flyerType]?.name || "TEARAWAY"} (${FORMATS[flyerType]?.size || "105 × 148"} ${
            FORMATS[flyerType]?.measuring || "mm"
          }) • Background blocks • Professional fonts • Unsplash images`}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendMessage}
          disabled={!inputText.trim() || isGenerating}
          className={classes.sendButton}
          startIcon={isGenerating ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {isGenerating ? "Generating..." : "Create Professional Design"}
        </Button>
      </div>
    </div>
  );
};

export const AIGeneration = React.memo(AIGenerationComponent);

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: theme.spacing(2),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  regenerateButton: {
    fontSize: "0.875rem",
    padding: "6px 12px",
  },
  expertModeAccordion: {
    marginBottom: theme.spacing(2),
    "& .MuiAccordionSummary-root": {
      minHeight: "48px",
    },
  },
  expertModeContent: {
    width: "100%",
  },
  expertSection: {
    marginBottom: theme.spacing(2),
  },
  expertInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.spacing(1),
      minHeight: "48px !important",
    },
  },
  expertSlider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  modelLink: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.875rem",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  expertSelect: {
    minHeight: "48px !important",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.spacing(1),
    },
  },
  messagesContainer: {
    flex: 1,
    overflow: "hidden",
    marginBottom: theme.spacing(2),
  },
  messages: {
    height: "100%",
    overflowY: "auto",
    padding: theme.spacing(1),
  },
  message: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    maxWidth: "90%", // Increased from 85% to better utilize wider sidebar
  },
  userMessage: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    marginLeft: "auto",
  },
  aiMessage: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  errorMessage: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    border: "1px solid #ef5350",
  },
  messageText: {
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  timestamp: {
    opacity: 0.7,
    marginTop: theme.spacing(0.5),
  },
  loadingSpinner: {
    marginRight: theme.spacing(1),
  },
  quickPrompts: {
    marginBottom: theme.spacing(2),
  },
  quickPromptsTitle: {
    display: "block",
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    justifyContent: "flex-start", // Better alignment for wider space
  },
  chip: {
    fontSize: "0.875rem",
    height: "28px",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
  inputContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%", // Ensure input takes full width
  },
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.spacing(1),
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.875rem",
      color: theme.palette.text.secondary,
    },
  },
  sendButton: {
    borderRadius: theme.spacing(1),
    fontWeight: 600,
    textTransform: "none",
    background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
    "&:hover": {
      background: "linear-gradient(45deg, #1976D2 30%, #0BB8E8 90%)",
    },
  },
}));
